import React, { useState, useRef, useEffect } from 'react';
import { Container, Row, Col, Form, Button, Card, Alert, Modal, Table } from 'react-bootstrap';
import { Link, useLocation, useHistory } from 'react-router-dom';
import ReactLoading from 'react-loading';
import Swal from 'sweetalert2';
import axios from 'axios';
import { BASE_URL } from "../../../constants/Endpoints"


const Quota = () => {
    const form = useRef(null);
    const location = useLocation();
    const history = useHistory();
    const [loading, setLoading] = useState(false);
    const [inputs, setInputs] = useState({
        codice_fiscale: '',
        chiave_pensione: ''
    });
    const [isFromUrl, setIsFromUrl] = useState(false);
    const [success, setSuccess] = useState(false);
    const [error, setError] = useState(null);
    const [contactId, setContactId] = useState(null);
    const [contactData, setContactData] = useState(null);
    const [quotaResponse, setQuotaResponse] = useState(null);
    const [showQuotaModal, setShowQuotaModal] = useState(false);

    // Get contact_id from URL params if present and fetch contact details
    useEffect(() => {
        const searchParams = new URLSearchParams(location.search);
        const contactIdParam = searchParams.get('contact_id');
        
        if (contactIdParam) {
            setContactId(contactIdParam);
            setIsFromUrl(true);
            // Fetch contact details
            const fetchContact = async () => {
                try {
                    const response = await axios({
                        method: 'get',
                        url: `${BASE_URL}/api-v1/people/contact/${contactIdParam}/view`,
                        withCredentials: true
                    });
                    
                    if (response.data) {
                        setContactData(response.data);
                        setInputs({ 
                            codice_fiscale: response.data.codice_fiscale || '',
                            chiave_pensione: response.data.chiave_pensione || ''
                        });
                    }
                } catch (err) {
                    console.error('Error fetching contact:', err);
                    setError('Errore nel recupero dei dati del contatto');
                }
            };
            
            fetchContact();
        }
    }, [location.search]);

    const handleChange = (event) => {
        const name = event.target.name;
        const value = event.target.value;
        setInputs(values => ({ ...values, [name]: value }));
    };

    const submitForm = async (event) => {
        event.preventDefault();
        setLoading(true);
        setError(null);
        setSuccess(false);
        
        try {
            const response = await axios({
                method: 'get',
                url: `https://censimenti-csq.jazeeautomation.com/valutazione/api/QuotaCedibile?codicefiscale=${inputs.codice_fiscale}&chiavepensione=${inputs.chiave_pensione}`,
                headers: {
                    'x-api-key': process.env.REACT_APP_CENSIMENTI_API_KEY || ''
                }
            });

            if (response.data && response.data.success !== false) {
                setQuotaResponse(response.data);
                setShowQuotaModal(true);
                
                // Save to backend
                try {
                    await axios({
                        method: 'post',
                        url: `${BASE_URL}/api-v1/people/quota/create`,
                        headers: {
                            'Content-Type': 'application/json'
                        },
                        data: {
                            contact: contactId, // Link to contact if available
                            request: { 
                                codicefiscale: inputs.codice_fiscale,
                                chiavepensione: inputs.chiave_pensione
                            },
                            response: response.data
                        },
                        withCredentials: true
                    });
                    console.log('Quota saved to backend');
                } catch (saveError) {
                    console.error('Error saving quota to backend:', saveError);
                    // Don't fail the main request if backend save fails
                }
            } else {
                const errorMsg = response.data.error || 'Errore sconosciuto';
                setError(errorMsg);
                Swal.fire({
                    icon: 'error',
                    title: 'Errore',
                    text: errorMsg,
                });
            }
        } catch (err) {
            console.error('Error fetching quota:', err);
            setError(err.response?.data?.message || 'Errore durante il calcolo della quota');
            Swal.fire({
                icon: 'error',
                title: 'Errore',
                text: 'Si è verificato un errore durante la ricerca',
            });
        } finally {
            setLoading(false);
        }
    };

    return (
        <>
            <Container fluid className='smooth-transition'>
                <Row>
                    <Col lg="12">
                        <div className="d-flex flex-wrap align-items-center justify-content-between mb-4">
                            <div>
                                <h4 className="mb-3">Quota Cedibile</h4>
                                <nav aria-label="breadcrumb">
                                    <ol className="breadcrumb p-0 mb-0">
                                        <li className="breadcrumb-item">
                                            <Link to="/">Dashboard</Link>
                                        </li>
                                        {contactId && (
                                            <li className="breadcrumb-item">
                                                <Link to={`/contact-view/${contactId}`}>
                                                    {contactData ? `${contactData.name} ${contactData.surname}` : 'Contatto'}
                                                </Link>
                                            </li>
                                        )}
                                        <li className="breadcrumb-item active" aria-current="page">Quota Cedibile</li>
                                    </ol>
                                </nav>
                            </div>
                            <Button variant="primary" className="btn-sm d-flex align-items-center" onClick={() => history.goBack()}>
                                <svg xmlns="http://www.w3.org/2000/svg" width="20" viewBox="0 0 20 20" fill="currentColor">
                                    <path fillRule="evenodd" d="M9.707 16.707a1 1 0 01-1.414 0l-6-6a1 1 0 010-1.414l6-6a1 1 0 011.414 1.414L5.414 9H17a1 1 0 110 2H5.414l4.293 4.293a1 1 0 010 1.414z" clipRule="evenodd" />
                                </svg>
                                <span className="ml-2">Indietro</span>
                            </Button>
                        </div>
                    </Col>
                    <Col lg="12">
                        <Card>
                            <Card.Body className="smooth-transition">
                                <Form className="g-3" onSubmit={submitForm} ref={form}>
                                    <div className='row'>
                                        <div className="col-md-6 mb-3">
                                            <Form.Label htmlFor='codice_fiscale' className="form-label font-weight-bold text-muted text-uppercase">
                                                Codice Fiscale
                                            </Form.Label>
                                            <Form.Control 
                                                type='text' 
                                                className="form-control" 
                                                name='codice_fiscale' 
                                                placeholder=''
                                                maxLength="50"
                                                value={inputs["codice_fiscale"]}
                                                onChange={handleChange}
                                                disabled={isFromUrl}
                                                required
                                            />
                                        </div>
                                        <div className="col-md-6 mb-3">
                                            <Form.Label htmlFor='chiave_pensione' className="form-label font-weight-bold text-muted text-uppercase">
                                                Chiave Pensione
                                            </Form.Label>
                                            <Form.Control 
                                                type='text' 
                                                className="form-control" 
                                                name='chiave_pensione' 
                                                placeholder=''
                                                maxLength="50"
                                                value={inputs["chiave_pensione"]}
                                                onChange={handleChange}
                                                disabled={isFromUrl}
                                                required
                                            />
                                        </div>
                                    </div>
                                    {isFromUrl && (
                                        <div className="row mb-3">
                                            <div className="col-12">
                                                <Form.Text className="text-muted">
                                                    Dati precompilati dal contatto
                                                </Form.Text>
                                            </div>
                                        </div>
                                    )}

                                    {loading ? 
                                        <Button type="button" disabled>
                                            <ReactLoading
                                                className="mx-auto"
                                                type={"spin"}
                                                color={"#fefefe"}
                                                height={24}
                                                width={24}
                                            />
                                        </Button> 
                                        : 
                                        <Button type="submit">Calcola Quota</Button>
                                    }
                                </Form>
                            </Card.Body>
                        </Card>
                    </Col>

                    {/* Error Display */}
                    {error && (
                        <Col lg="12" className="mt-3">
                            <Alert variant="danger">
                                {error}
                            </Alert>
                        </Col>
                    )}

                    {/* Quota Response Modal */}
                    <Modal show={showQuotaModal} onHide={() => setShowQuotaModal(false)} size="xl">
                        <Modal.Header closeButton>
                            <Modal.Title>Risultato Calcolo Quota Cedibile</Modal.Title>
                        </Modal.Header>
                        <Modal.Body>
                            {quotaResponse && quotaResponse["Inps/Inpdap"] && (
                                <Table striped bordered hover size="sm" responsive>
                                    <thead>
                                        <tr>
                                            <th>Istituto</th>
                                            <th>Chiave Pensione</th>
                                            <th>Codice Fiscale</th>
                                            <th>Esito</th>
                                            <th>Importo</th>
                                            <th>Nota</th>
                                            <th>Documento</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        <tr>
                                            <td>INPS/INPDAP</td>
                                            <td>{quotaResponse["Inps/Inpdap"].chiave_pensione || '-'}</td>
                                            <td>{inputs.codice_fiscale || '-'}</td>
                                            <td className="text-center">
                                                {quotaResponse["Inps/Inpdap"].esito === 'OK' ? (
                                                    <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" fill="green" viewBox="0 0 16 16">
                                                        <path d="M10.97 4.97a.75.75 0 0 1 1.07 1.05l-3.99 4.99a.75.75 0 0 1-1.08.02L4.324 8.384a.75.75 0 1 1 1.06-1.06l2.094 2.093 3.473-4.425a.267.267 0 0 1 .02-.022z"/>
                                                    </svg>
                                                ) : (
                                                    <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" fill="red" viewBox="0 0 16 16">
                                                        <path d="M4.646 4.646a.5.5 0 0 1 .708 0L8 7.293l2.646-2.647a.5.5 0 0 1 .708.708L8.707 8l2.647 2.646a.5.5 0 0 1-.708.708L8 8.707l-2.646 2.647a.5.5 0 0 1-.708-.708L7.293 8 4.646 5.354a.5.5 0 0 1 0-.708z"/>
                                                    </svg>
                                                )}
                                            </td>
                                            <td>{quotaResponse["Inps/Inpdap"].importo || '-'}</td>
                                            <td>-</td>
                                            <td>
                                                {quotaResponse["Inps/Inpdap"].pdfDocumento && (
                                                    <Button 
                                                        variant="outline-primary" 
                                                        size="sm"
                                                        onClick={() => {
                                                            const link = document.createElement('a');
                                                            link.href = 'data:application/pdf;base64,' + quotaResponse["Inps/Inpdap"].pdfDocumento;
                                                            link.download = 'quota_cedibile.pdf';
                                                            link.click();
                                                        }}
                                                    >
                                                        Scarica PDF
                                                    </Button>
                                                )}
                                            </td>
                                        </tr>
                                    </tbody>
                                </Table>
                            )}
                        </Modal.Body>
                        <Modal.Footer>
                            <Button variant="secondary" onClick={() => setShowQuotaModal(false)}>
                                Chiudi
                            </Button>
                        </Modal.Footer>
                    </Modal>
                </Row>
            </Container>
        </>
    );
};

export default Quota;